import { setLoginRes } from "@/api";
import { zodResolver } from "@hookform/resolvers/zod";
import classNames from "classnames";
import React, { useCallback, useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  createPasswordInput,
  createPasswordSchema,
  setNewPasswordAPI,
  verifyVerificationCode,
} from "../../services/user";
import Button from "../Common/Buttons/Button";
import PasswordInputField from "../Common/Input/PasswordInputField";
import { customToast } from "../Common/Toast";
import {
  CreatePasswordErrorState,
  LoadingState,
} from "./ForgotPassword/ForgotPasswordComponents";

const VERIFICATION_STATUS = {
  LOADING: "loading",
  SUCCESS: "success",
  ERROR: "error",
} as const;

type VerificationStatus =
  (typeof VERIFICATION_STATUS)[keyof typeof VERIFICATION_STATUS];

const CreatePasswordForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);

  const hashParts = location.hash.split("?");
  const queryString = hashParts.length > 1 ? hashParts[1] : "";
  const params = new URLSearchParams(queryString);
  const employeeData = params.get("employee");

  const [verificationStatus, setVerificationStatus] =
    useState<VerificationStatus>(VERIFICATION_STATUS.LOADING);
  const [userAccountID, setUserAccountID] = useState<string | null>(null);
  const [resetToken, setResetToken] = useState<string | null>(null);

  const methods = useForm<createPasswordInput>({
    resolver: zodResolver(createPasswordSchema),
    mode: "onChange",
  });

  const { handleSubmit, formState } = methods;

  useEffect(() => {
    if (employeeData) {
      const decodedData = atob(employeeData);
      const jsonData = JSON.parse(decodedData);
      const resetTokenFromData = jsonData.password;

      if (resetTokenFromData) {
        setResetToken(resetTokenFromData);
        handleTokenVerification(resetTokenFromData);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeData]);

  const handleTokenVerification = useCallback(async (resetCode: string) => {
    try {
      const response = await verifyVerificationCode(resetCode);

      if (response.status !== 200) {
        throw new Error("Verification failed");
      }

      if (response.data === "Record Not found into database") {
        setVerificationStatus(VERIFICATION_STATUS.ERROR);
        return;
      }

      const userID = response.data?.item2?.userAccountID;
      if (!userID) {
        throw new Error("UserAccountID not found in item2");
      }

      setLoginRes(response.data.item1);
      setUserAccountID(userID.toString());
      //setResetToken(response.data?.item2?.token);
      setVerificationStatus(VERIFICATION_STATUS.SUCCESS);
    } catch {
      setVerificationStatus(VERIFICATION_STATUS.ERROR);
    }
  }, []);

  const onSubmit = async (data: createPasswordInput) => {
    setLoading(true);
    try {
      const trimmedPassword = data.password.trim();

      if (!resetToken) {
        throw new Error("Reset token not found");
      }

      await setNewPasswordAPI(trimmedPassword, userAccountID!, resetToken);
      //dispatch(updateDefaultPasswordStatus("True"));

      customToast(t("auth.create_password.success_message"), "success");
      navigate("/login");
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    switch (verificationStatus) {
      case VERIFICATION_STATUS.LOADING:
        return <LoadingState />;
      case VERIFICATION_STATUS.ERROR:
        return <CreatePasswordErrorState />;
      case VERIFICATION_STATUS.SUCCESS:
        return (
          <div className="font-outfit mx-auto w-full sm:w-[380px] md:w-[420px] md:mt-[40px]">
            <div className="shadow-lg bg-white rounded-[24px] p-[32px]">
              <h1 className="block font-semibold text-[32px] leading-[127%] ">
                {t("auth.create_password.title")}
              </h1>
              <p className="py-[16px] text-[18px] leading-[127%]">
                {t("auth.create_password.description")}
              </p>
              <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="flex flex-col text-[18px] mb-6">
                    <Controller
                      control={methods.control}
                      name="password"
                      render={({ field: { value, onChange } }) => (
                        <PasswordInputField
                          id="password"
                          type="password"
                          name="password"
                          value={value}
                          onChange={onChange}
                          label="Password"
                          placeholder="Password"
                          isNewPassword={true}
                        />
                      )}
                    />
                  </div>

                  {/* <button
                    type="submit"
                    //  onClick={handleCreatePassword}
                    className={classNames(
                      "w-full bg-teal-500 text-white px-4 rounded-lg hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-opacity-50 py-4",
                      !formState.isValid && "opacity-50 cursor-not-allowed"
                    )}
                  >
                    Create new Password
                  </button>
 */}
                  <Button
                    type="submit"
                    iconVisibility="hide"
                    ariaLabel="login"
                    label={t("auth.create_password.button")}
                    loading={loading}
                    className={classNames(
                      !formState.isValid && "opacity-50 cursor-not-allowed",
                    )}
                    //handleClick={() => void onFormSubmit()}
                  />
                </form>
              </FormProvider>
            </div>
          </div>
        );
    }
  };

  return renderContent();
};

export default CreatePasswordForm;
